import { UUID } from "crypto";

export interface ISquare {
  Pieces: IPiece[];
}

// TODO could we use the type system to prevent off board coords altogether
export interface Position {
  row: number;
  col: number;
}

export enum PieceType {
  Bear = "Bear",
  Crane = "Crane",
  Frog = "Frog",
  Fungi = "Fungi",
  Goat = "Goat",
  Monkey = "Monkey",
  Octopus = "Octopus",
  Penguin = "Penguin",
  Rhino = "Rhino",
  Snake = "Snake",
  Tree = "Tree",
  Wolf = "Wolf",
  Virus = "Virus",
}

export type Direction =
  | "up"
  | "up-right"
  | "right"
  | "down-right"
  | "down"
  | "down-left"
  | "left"
  | "up-left";

export interface IPiece {
  id: string;
  type: PieceType;
  isLight: boolean;
  isKing: boolean;
  poisonedStatus: {
    isPoisoned: boolean;
    removeOnTurn?: number;
  };

  zombifiedStatus: {
    isZombified: boolean;
    removeOnTurn?: number;
  };
  isZombified: boolean;
  isFainted: boolean;
  isEntangled: boolean;
  isShadowed: boolean;
  isShadow: boolean;
  isZombieInkShadow: boolean;  // True if this shadow was created by a zombified octopus
  zombieInkTargetColor?: boolean; // The color (true=Light) that must deal with these shadows
  isInfected: boolean;
  direction: Direction | null;
  hasCharged: boolean;
  hasMoved: boolean;
  hasShed: boolean;
  hasLilyJumped: boolean;
  isMeadowed: boolean;
  isRiding: boolean;
  isHousing: boolean;
  isLone: boolean;
  isDormant: boolean;
  entangledPieceId?: string;
  getValidMovesForPiece(
    boardState: ISquare[][],
    position: Position,
    isLightTurn?: boolean,
    gameStateManager?: any
  ): IMove[];
  movementStrategy: PieceMovement;
}

export interface IMove {
  id: UUID;
  pieceId: string;
  position: Position;
  toPiece: IPiece | null;
  moveType: MoveType;
  sideEffects: ISideEffect[];
}

export interface ISideEffect {
  id: UUID;
  moveId: UUID;
  position: Position;
  piecesToEffect: IPiece[];
  effect: Effect;
}

export class Effect {
  type: EffectType;
  activeTurns?: number;

  private constructor(
    type: EffectType,
    activeTurns: number | undefined = undefined
  ) {
    this.type = type;
    this.activeTurns = activeTurns;
  }

  static Entangle(): Effect {
    return new Effect(EffectType.entangle);
  }

  static Capture(): Effect {
    return new Effect(EffectType.capture);
  }

  static Poison(): Effect {
    // Poison lasts 4 turn increments (applied at turn N, removed when turn reaches N+4):
    // Turn N: Applied, N+1: Poisoned, N+2: Poisoned, N+3: Poisoned, N+4: Removed (piece can move)
    return new Effect(EffectType.poison, 4);
  }

  static Meadow(): Effect {
    return new Effect(EffectType.meadow);
  }

  static HouseMove(): Effect {
    return new Effect(EffectType.housemove);
  }

  static BonusTurn(): Effect {
    return new Effect(EffectType.bonusTurn);
  }

  static Shadow(): Effect {
    return new Effect(EffectType.shadow);
  }

  static Zombify(): Effect {
    // Zombification lasts through opponent's turn only
    // Applied at turn T, should be removed at turn T+2
    // Example: Applied at turn 0, removed at turn 2
    // - Turn 0: Light captures, zombification applied
    // - Turn 1: Dark controls zombified piece
    // - Turn 2: Zombification removed when Light's turn starts
    return new Effect(EffectType.zombify, 2);
  }
}

export enum EffectType {
  capture = "Capture",
  meadow = "Meadow",
  entangle = "Entangle",
  housemove = "House Move",
  infect = "Infect",
  respawn = "Respawn",
  poison = "Poison",
  bonusTurn = "Bonus Turn",
  shadow = "Shadow",
  zombify = "Zombify",
  rotate = "Rotate",
}

export enum MoveType {
  noMove = "No Move",
  regular = "Regular",
  capture = "Capture",
  tripleentangle = "Triple Entangle",
  ink = "Ink",
  house = "House",
  traverse = "Traverse",
  faint = "Faint",
  jumppad = "Jump Pad",
  lane = "Lane",
  kong = "Kong",
  swing = "Swing",
  rotate = "Rotate",
  trample = "Trample",
  infect = "Infect",
  leap = "Leap",
  endTurn = "End Turn",
  consume = "Consume",
}

export interface GameState {
  mode: Mode;
  board: ISquare[][];
  state: State;
  lightKing?: PieceType;
  darkKing?: PieceType;
  lightPlayer?: string;
  darkPlayer?: string;
  lightPlayerUsername?: string;
  darkPlayerUsername?: string;
  isLightTurn: boolean;
  currentPlayer?: "white" | "black"; // For MCTS v2 compatibility
  turnCount: number;
  lightWon?: boolean;
  skipTurnChange?: boolean;
  fenHistory: string[];
  activeBonusTurnPieceId?: string | null;
  zombieRacePieceType?: PieceType;
  lastMonkeyGambleFailure?: {
    diceRoll: number;
    dangerCount: number;
    jumpedEnemies: Position[];
    bearCapturePosition?: Position;
  };
  monkeyGambleLossEffectPosition?: Position;
  lastMoveWasLight?: boolean; // Track the color of the last move for FEN delta inference
  matchmakingMode?: "competitive" | "casual"; // Track if game is ranked or casual
  lightPlayerMatchmakingMode?: "competitive" | "casual" | "placement"; // Light player's original matchmaking mode
  darkPlayerMatchmakingMode?: "competitive" | "casual" | "placement"; // Dark player's original matchmaking mode
  timeControlPreset?: string; // Track which time control preset was used (sprint/standard/marathon)
  shadowsActive?: boolean; // Track if shadows exist and should be removed on next move (for Firebase sync)

  // Immediate swing return block - prevents infinite swing loops
  lastSwingFromPosition?: Position | null; // Where the last swing originated
  lastSwingToPosition?: Position | null; // Where the last swing landed
  lastSwingMonkeyId?: string | null; // Which monkey made the last swing

  // Player presence tracking for disconnect detection
  lightPlayerLastSeen?: number; // Timestamp of last activity from light player
  darkPlayerLastSeen?: number; // Timestamp of last activity from dark player
  lightPlayerTabHidden?: boolean; // True if light player's browser tab is hidden/closed
  darkPlayerTabHidden?: boolean; // True if dark player's browser tab is hidden/closed

  // Bot game fields - set when matchmaking falls back to a bot opponent
  isBotGame?: boolean;
  botPlayerId?: string; // UID of the bot account
  botAiModel?: string; // AIModel enum value (e.g. "sentinel")
  botTimeFrame?: string; // TimeFrame enum value (e.g. "fast")
}

export enum Mode {
  Dev = "Dev",
  Live = "Live",
  AI = "AI",
  TestAI = "TestAI",
  Watch = "Watch",
  Tutorial = "Tutorial",
  Sandbox = "Sandbox",
  Rules = "Rules",
}

export enum State {
  None = "None",
  Searching = "Searching",
  KingSelection = "King Selection",
  PieceSelection = "Piece Selection",
  Playing = "Playing",
  PostGame = "Post Game",
  Terminated = "Terminated",
  Draw = "Draw",
}

export type GameStateListener = () => void;

export interface PieceMovement {
  getValidMoves(
    piece: IPiece,
    position: Position,
    boardState: ISquare[][],
    isLightTurn?: boolean,
    gameStateManager?: any
  ): IMove[];

  //canCapture(piece: IPiece, target: IPiece): boolean;
}

// MCTS v2 simplified types for race analysis
export interface Board {
  squares: (IPiece | null)[]; // 64-element array representing 8x8 board
}

export interface Move {
  from: number; // Square index (0-63)
  to: number; // Square index (0-63)
  piece: IPiece; // Piece being moved
  captured: IPiece | null; // Piece being captured (if any)
}
